import { buildSSRSearchParameterSerializer } from '@coveo/headless-react/ssr';
import { parseUrlPageSize } from '../../utils/pageSize';

export type RouteSearchParams = {
  [key: string]: string | string[] | undefined;
};

/**
 * Coveo scalar params the search engine is allowed to read from a URL. The
 * serializer recognizes more (`aq`/`cq` are raw query-syntax injection points,
 * `debug` embeds Coveo's execution report in the response, `tab` has no tab
 * controller), and anything not listed here is dropped before it reaches the
 * engine.
 */
const ALLOWED_SCALAR_PARAMS = new Set([
  'q',
  'sortCriteria',
  'firstResult',
  'numberOfResults',
]);

/**
 * Facet param prefixes of the facet TYPES the SSR path consumes (flat,
 * excluded, numeric, date). Field names are deliberately unrestricted: facet
 * controllers register task by task through the rollout, and an unregistered
 * field can only filter public catalog data.
 */
const ALLOWED_FACET_PREFIX = /^(f|fExcluded|nf|df)-.+/;

/**
 * The URL keyspace the engine may consume — the domain sanitizeSearchParams
 * filters within. The legacy-hash migration treats the CSR hash as
 * authoritative for exactly this keyspace: a key the hash no longer carries
 * was removed during the CSR session, so a stale query-string copy of it must
 * be dropped, not merged around.
 */
export const isEngineSearchParam = (key: string): boolean =>
  ALLOWED_SCALAR_PARAMS.has(key) || ALLOWED_FACET_PREFIX.test(key);

/**
 * Far beyond any real pagination depth (417 pages at 24/page) while staying
 * well under the Coveo API's index limits, so a crafted offset can't turn into
 * a rejected server-side query.
 */
const MAX_FIRST_RESULT = 10_000;

/**
 * Filter URL-sourced params down to what the engine may consume. The engine's
 * own handling is not safe on raw input: `numberOfResults` has no upper bound
 * (request amplification, so it is clamped to MAX_NUMBER_OF_RESULTS), and a
 * non-numeric `firstResult` fails engine validation — degrading the page and
 * firing Sentry on any crafted URL. For scalars the first occurrence wins (a
 * redirect chain or double-submitted form can repeat `q`); facet params keep
 * every occurrence (multi-select). Empty values carry no state and are dropped
 * everywhere: keeping `q=''` would make toLegacyHashFragment truthy and let the
 * CSR bridge overwrite a real anchor (`/search?q=#main`).
 */
export function sanitizeSearchParams(params: URLSearchParams): URLSearchParams {
  const sanitized = new URLSearchParams();
  params.forEach((value, key) => {
    if (!value) {
      return;
    }
    if (ALLOWED_FACET_PREFIX.test(key)) {
      sanitized.append(key, value);
      return;
    }
    if (!ALLOWED_SCALAR_PARAMS.has(key) || sanitized.has(key)) {
      return;
    }
    if (key === 'firstResult') {
      const offset = Number(value);
      if (Number.isInteger(offset) && offset >= 0) {
        sanitized.set(key, String(Math.min(offset, MAX_FIRST_RESULT)));
      }
      return;
    }
    if (key === 'numberOfResults') {
      const pageSize = parseUrlPageSize(value);
      if (pageSize !== undefined) {
        sanitized.set(key, String(pageSize));
      }
      return;
    }
    sanitized.set(key, value);
  });
  return sanitized;
}

function fromRouteSearchParams(
  searchParams: RouteSearchParams
): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(searchParams).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }
    const values = Array.isArray(value) ? value : [value];
    values.forEach((v) => params.append(key, v));
  });
  return params;
}

/**
 * Snap a URL offset onto the page grid the engine will actually paginate on.
 * The effective page size is deliberately kept out of serialized URLs
 * (registerNumberOfResultsAsDefault) unless the URL carries a custom size, so
 * `firstResult` usually travels alone and an offset written at one size can be
 * restored under another — `firstResult=24` shared from a 24/page session and
 * opened in a 48/page one, which the page-size experiment makes an everyday
 * cross-cohort case. Coveo derives the page as
 * Math.round(firstResult / numberOfResults) + 1, so an unaligned offset lands
 * the engine between pages: the pager reports page 2 while the grid shows
 * results 25-72, and the next page click jumps to 97, leaving 73-96
 * unreachable. Snapping down keeps the offset's first result on screen.
 */
function alignFirstResultToPageSize(
  params: URLSearchParams,
  defaultPageSize: number
): void {
  const firstResult = Number(params.get('firstResult'));
  const pageSize = Number(params.get('numberOfResults')) || defaultPageSize;
  if (!firstResult || !pageSize) {
    return;
  }
  params.set(
    'firstResult',
    String(Math.floor(firstResult / pageSize) * pageSize)
  );
}

/**
 * Convert a URL's params into the Coveo SearchParameters the engine may
 * consume: sanitized through the allowlist above, then aligned to
 * `defaultPageSize` — the page size the engine falls back to for a URL that
 * carries no `numberOfResults` (restoreSearchParameters reads its
 * `defaultNumberOfResults`). Both sides of the URL <-> engine sync go through
 * here, so the server seeding and the client `synchronize` resolve the same
 * URL to the same page.
 *
 * Keeps the @coveo/headless-react/ssr serializer confined to the headless/
 * layer so components stay off the raw Coveo API (see the SearchV2
 * no-restricted-imports guard).
 */
export function toEngineSearchParameters(
  params: URLSearchParams,
  defaultPageSize: number
) {
  const sanitized = sanitizeSearchParams(params);
  alignFirstResultToPageSize(sanitized, defaultPageSize);
  return buildSSRSearchParameterSerializer().toSearchParameters(sanitized);
}

/**
 * Convert Next.js route searchParams into the initial Coveo SearchParameters
 * used to seed fetchStaticState (SSR).
 *
 * The client-side URL write-back lives in SearchEngineProvider, which is also
 * in headless/ and uses the same serializer's `serialize`.
 */
export function toInitialSearchParameters(
  searchParams: RouteSearchParams,
  defaultPageSize: number
) {
  return toEngineSearchParameters(
    fromRouteSearchParams(searchParams),
    defaultPageSize
  );
}

/**
 * Encode the sanitized Coveo params in the CSR page's hash format, for the CSR
 * degrade path (CsrFallback). Same param names, but the v1 hash serializer
 * percent-encodes each value individually ('+' is a literal plus, not a space)
 * and comma-joins multi-values, where the query string form-encodes and
 * repeats keys. Keys are encoded too, so a crafted key (`f-x&q=evil` after
 * Next's decoding) can't split into extra hash params. Empty when the URL
 * carries no usable search state.
 */
export function toLegacyHashFragment(searchParams: RouteSearchParams): string {
  const sanitized = sanitizeSearchParams(fromRouteSearchParams(searchParams));
  return Array.from(new Set(sanitized.keys()))
    .map((key) => {
      const values = sanitized
        .getAll(key)
        .map((value) => encodeURIComponent(value))
        .join(',');
      return `${encodeURIComponent(key)}=${values}`;
    })
    .join('&');
}

/**
 * Whether the URL carries any state the engine would consume — the boundary
 * between the indexable bare page and a result permutation. Tracking,
 * affiliate and invalid params are sanitized away, so they never count.
 */
export function hasEngineSearchState(searchParams: RouteSearchParams): boolean {
  return (
    sanitizeSearchParams(fromRouteSearchParams(searchParams)).toString() !== ''
  );
}
